import { LabeledSection } from './LabeledSection';
import { i18n } from 'app/utils/i18n';
import { TagWithMetadata, useTagAnalysis } from 'app/api/tag-queries';

interface AnalysisProps {
  activeTag: TagWithMetadata;
  documentSuperId: number | undefined;
  onSelectTag: (tag: TagWithMetadata | null) => void;
  documentTags: TagWithMetadata[];
}

const formatDate = (dateValue: string) =>
  new Date(dateValue).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short', year: 'numeric' }).replace(/\./g, '');

const formatPriorPeriod = (
  priorPeriod: { periodStartDate: string | null; periodEndDate: string | null; periodInstant: string | null },
  toLabel: string
): string => {
  if (priorPeriod.periodInstant) return formatDate(priorPeriod.periodInstant);
  if (priorPeriod.periodStartDate && priorPeriod.periodEndDate) {
    return `${formatDate(priorPeriod.periodStartDate)} ${toLabel} ${formatDate(priorPeriod.periodEndDate)}`;
  }
  return '';
};

const formatPercent = (pct: number) => Math.abs(pct).toLocaleString('sv-SE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%';

const formatSignedAbsolute = (delta: number) => {
  const sign = delta >= 0 ? '+' : '-';
  return sign + Math.abs(Math.round(delta)).toLocaleString('sv-SE');
};

const iconClass = (direction: 'up' | 'down' | 'flat'): string => {
  if (direction === 'up') return 'fa-light fa-circle-arrow-up';
  if (direction === 'down') return 'fa-light fa-circle-arrow-down';
  return 'fa-light fa-circle-arrow-right';
};

export const Analysis = ({ activeTag, documentSuperId, onSelectTag, documentTags }: AnalysisProps) => {
  const { data } = useTagAnalysis(documentSuperId, activeTag.id);
  if (!data || !data.hasComparison) return null;

  const priorTag = documentTags.find((t) => t.id === data.priorTagId);
  if (!priorTag) return null;

  const directionLabel = data.direction === 'up' ? i18n('tag.analysis.up') : data.direction === 'down' ? i18n('tag.analysis.down') : i18n('tag.analysis.flat');

  const magnitudeText = data.deltaPct === null ? formatSignedAbsolute(data.deltaAbs) : formatPercent(data.deltaPct);

  const periodText = formatPriorPeriod(data.priorPeriod, i18n('tag.analysis.to'));
  const fromLabel = i18n('tag.analysis.from');

  return (
    <LabeledSection label={i18n('tag.analysis.title')} style={{ display: 'flex', gap: '0.5rem', fontSize: '12px' }}>
      <button type="button" className={`tag-info__analysis-link tag-info__analysis-link--${data.direction}`} onClick={() => onSelectTag(priorTag)}>
        <i className={`${iconClass(data.direction)} fa-xl tag-info__analysis-link__icon`} aria-hidden="true" />
        <strong>
          {directionLabel} {magnitudeText}
        </strong>{' '}
      </button>
      {fromLabel} {periodText}
    </LabeledSection>
  );
};
